/**
 * SCHEDULE — desktop (migration 3071; DESIGN-CHANGELOG 2026-10-14 "SCHEDULE
 * ships into both mirrors"). `design/GT Schedule.dc.html`, the `inScreen` half:
 *
 *  - `Schedule` — the month at rest: Monday-first, weekend nights outlined, a
 *    cell shows who can play and up to four nominations with their windows,
 *    tonight is tagged. Clicking an upcoming night opens it.
 *  - `SchedNight` — the night page, full screen (`DesktopApp` hides the nav on
 *    `isSchedDay`): START GAME NIGHT and + NOMINATE on one row, the guide
 *    beneath at full width — a row per nominator, a block per nomination
 *    across its window, backers in its corner, the line is NOW. A block opens
 *    the game modal (`desktop/modals/SchedModals.tsx`).
 *
 * Paint only: `scheduleVM` is the whole model.
 */

import { useEffect } from 'react';

import { Avatar } from '../../components/Avatar.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { QuarterIcon } from '../../components/QuarterIcon.tsx';
import { SCH_SCRIM, SchCover, SchFace, SchFaceStack, useSchedClock } from '../../components/SchedKit.tsx';
import { useQueueArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { scheduleVM } from '../../store/selectors.schedule.ts';
import type { SchNightVM } from '../../store/selectors.schedule.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';
const SANS = 'var(--gt-font-sans)';

export default function Schedule(): JSX.Element {
  const s = useQd();
  const now = useSchedClock();
  const v = scheduleVM(s, now);
  const { refreshSched, schY, schM } = s;
  useEffect(() => {
    void refreshSched();
  }, [refreshSched, schY, schM]);
  useQueueArt(Object.values(s.sched).flat().map((n) => n.id));

  return (
    <div data-screen-label="Schedule" style={{ display: 'flex', flexDirection: 'column', flex: 1, height: 'calc(100vh - 58px)', minHeight: 520, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 26px 0', flex: 'none', fontFamily: MONO, fontSize: 10, color: 'var(--gt-muted)', flexWrap: 'wrap', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', fontFamily: MONO, fontSize: '10.5px' }}>
          <div onClick={() => s.schMonthGo(-1)} className="d-hov-acc-box" style={{ border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-5)', padding: '8px 11px', whiteSpace: 'nowrap', cursor: 'pointer' }}>
            ◂
          </div>
          <div style={{ position: 'relative', zIndex: 2, marginLeft: -1, border: '1px solid var(--acc)', color: 'var(--acc)', background: 'var(--accSoft)', padding: '8px 12px', whiteSpace: 'nowrap', minWidth: 140, textAlign: 'center' }}>
            {v.monthTxt}
          </div>
          <div onClick={() => s.schMonthGo(1)} className="d-hov-acc-box" style={{ marginLeft: -1, border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-5)', padding: '8px 11px', whiteSpace: 'nowrap', cursor: 'pointer' }}>
            ▸
          </div>
        </div>
        <span style={{ letterSpacing: '.06em' }}>CLICK A NIGHT TO OPEN IT</span>
        <div style={{ flex: 1 }} />
        <span style={{ border: '1px solid var(--acc)', background: 'var(--accSoft)', padding: '7px 9px', color: 'var(--acc)', whiteSpace: 'nowrap' }}>TONIGHT · {v.tonightTxt}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '14px 26px 18px', boxSizing: 'border-box', flex: 1, minHeight: 0 }}>
        <div style={{ display: 'flex', gap: 5, flex: 'none' }}>
          {v.head.map((h) => (
            <div key={h.key} style={{ flex: 1, minWidth: 0, padding: '0 8px 6px', borderBottom: '1px solid var(--gt-line)', fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.1em', color: h.clr }}>
              {h.l}
            </div>
          ))}
        </div>
        {v.weeks.map((w) => (
          <div key={w.key} style={{ display: 'flex', gap: 5, flex: 1, minHeight: 0 }}>
            {w.cells.map((c) => (
              <div
                key={c.key}
                onClick={c.can ? () => s.schOpenDay(c.k) : undefined}
                className={c.can ? 'd-hov-bd3' : undefined}
                style={{
                  flex: 1,
                  minWidth: 0,
                  boxSizing: 'border-box',
                  containerType: 'size',
                  border: c.bd,
                  background: c.bg,
                  boxShadow: c.sh,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'clamp(4px,4cqh,10px)',
                  padding: 'clamp(7px,6cqh,16px) clamp(8px,5cqw,18px)',
                  overflow: 'hidden',
                  cursor: c.cur,
                  position: 'relative',
                  zIndex: c.z,
                  opacity: c.op,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: PIXEL, fontSize: c.nFs, color: c.nClr, transform: 'translateY(0.13em)' }}>{c.n}</span>
                  {c.today ? (
                    <span style={{ fontFamily: PIXEL, fontSize: 6, lineHeight: 1, color: 'var(--gt-ink)', background: 'var(--acc)', padding: '3px 4px 2px', whiteSpace: 'nowrap' }}>TONIGHT</span>
                  ) : null}
                  <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 'clamp(7.5px,6cqh,10px)', letterSpacing: '.06em', color: 'var(--gt-muted-2)', whiteSpace: 'nowrap' }}>{c.sum}</span>
                </div>
                {c.facesOn ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {c.faces.map((q) => (
                        <SchFace key={q.key} f={q} px="clamp(12px,10cqh,20px)" fs="clamp(5px,4cqh,8px)" style={{ marginLeft: q.ml }} />
                      ))}
                    </div>
                    <span style={{ fontFamily: MONO, fontSize: 'clamp(7px,5.5cqh,9.5px)', letterSpacing: '.05em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{c.qTxt}</span>
                  </div>
                ) : null}
                {c.lines.map((ln) => (
                  <div key={ln.key} style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                    <SchCover
                      id={ln.id}
                      cands={ln.coverCands}
                      fall={ln.fall}
                      title={ln.nm}
                      style={{ width: 'clamp(14px,12cqh,26px)', height: 'clamp(19px,16cqh,35px)', flex: 'none', boxSizing: 'border-box', border: '1px solid var(--gt-ink)' }}
                    />
                    <span style={{ flex: 1, minWidth: 0, fontFamily: SANS, fontSize: 'clamp(9.5px,8cqh,14px)', fontWeight: 500, color: 'var(--gt-text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ln.nm}</span>
                    <span style={{ flex: 'none', fontFamily: MONO, fontSize: 'clamp(6.5px,5.5cqh,9.5px)', letterSpacing: '.04em', color: ln.rClr, whiteSpace: 'nowrap' }}>{ln.range}</span>
                  </div>
                ))}
                {c.moreOn ? (
                  <span style={{ fontFamily: MONO, fontSize: 'clamp(7px,5.5cqh,9.5px)', letterSpacing: '.06em', color: 'var(--gt-muted-2)' }}>{c.moreTxt}</span>
                ) : null}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SchedNight(): JSX.Element | null {
  const s = useQd();
  const now = useSchedClock();
  const v = scheduleVM(s, now);
  const d = v.day;
  useQueueArt((d ? s.sched[d.k] ?? [] : []).map((n) => n.id));
  if (!d) return null;

  return (
    <div data-screen-label="Schedule night" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '100vh', background: 'var(--gt-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '15px 24px', borderBottom: '1px solid var(--gt-line)', background: 'var(--gt-panel)', flexWrap: 'wrap' }}>
        <div
          onClick={() => s.schBack()}
          className="d-hov-acc"
          style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', whiteSpace: 'nowrap', cursor: 'pointer' }}
        >
          ◂ BACK TO SCHEDULE
        </div>
        <div style={{ width: 1, height: 24, background: 'var(--gt-border-2)', flex: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, flex: 'none' }}>
          <QuarterIcon u={2} hover={false} />
          <span style={{ fontFamily: PIXEL, fontSize: 13, color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>{d.ttl}</span>
          {d.isToday ? (
            <span style={{ fontFamily: PIXEL, fontSize: 7, lineHeight: 1, color: 'var(--gt-ink)', background: 'var(--acc)', padding: '4px 5px 3px', whiteSpace: 'nowrap' }}>TONIGHT</span>
          ) : null}
        </div>
        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', color: 'var(--gt-muted)' }}>{d.meta}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '22px 26px 30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {d.isToday ? (
            <>
              <PixelButton label="START GAME NIGHT ▸" tone="acc" variant={d.startVar} fs={9} pad="14px 18px" nowrap fit lh="1" busy={s.rBusy} onClick={() => void s.schStart()} />
              <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: 'var(--gt-muted-2)' }}>{d.startSub}</span>
            </>
          ) : null}
          {d.notToday ? <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: 'var(--gt-muted)' }}>{d.laterTxt}</span> : null}
          {d.isPast ? <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: 'var(--gt-muted)' }}>{d.laterTxt}</span> : null}
          {/* A night that has passed takes no new nominations — the server
              refuses them, so the button would only ever say no. */}
          {d.isPast ? null : (
            <div style={{ marginLeft: 'auto' }}>
              <PixelButton label="+ NOMINATE" fs={8} pad="13px 14px" nowrap fit lh="1" variant="outline" onClick={() => s.schNomStart()} />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.1em', color: 'var(--gt-muted-2)' }}>{d.nomHead}</span>
          {d.nomEmpty ? (
            <div style={{ border: '1px dashed var(--gt-border-3)', padding: 14, fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.07em', color: 'var(--gt-muted-2)' }}>NOTHING NOMINATED YET</div>
          ) : null}
          {d.nomOn ? <Guide d={d} /> : null}
        </div>
      </div>
    </div>
  );
}

/** The guide: a row per nominator, a block per nomination across its window, NOW as a line. */
function Guide({ d }: { d: SchNightVM }): JSX.Element {
  const open = useQd((s) => s.schOpenGame);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ width: '100%', position: 'relative' }}>
        <div style={{ display: 'flex', height: 26, alignItems: 'flex-end' }}>
          <div style={{ width: 76, flex: 'none' }} />
          <div style={{ flex: 1, position: 'relative', height: '100%' }}>
            {d.gHours.map((k) => (
              <span key={k.key} style={{ position: 'absolute', left: k.leftP, bottom: 6, transform: k.tx, fontFamily: MONO, fontSize: 7, letterSpacing: '.05em', color: k.clr, whiteSpace: 'nowrap' }}>
                {k.l}
              </span>
            ))}
            {d.nowOn ? (
              <div style={{ position: 'absolute', left: d.nowXP, bottom: 2, transform: 'translateX(-50%)', background: 'var(--acc)', color: 'var(--gt-ink)', fontFamily: MONO, fontSize: '7.5px', fontWeight: 600, letterSpacing: '.06em', padding: '3px 6px', whiteSpace: 'nowrap', zIndex: 3 }}>
                {d.nowPill}
              </div>
            ) : null}
          </div>
        </div>
        {d.rows.map((r) => (
          <div key={r.key} style={{ display: 'flex', borderTop: '1px solid var(--gt-border)' }}>
            <div style={{ width: 76, flex: 'none', borderRight: '1px solid var(--gt-border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 4px', boxSizing: 'border-box' }}>
              <Avatar size={20} fs={7} ini={r.ini} bg={r.bg} hasAv={r.hasAv} avBg={r.avBg} />
              <span style={{ fontFamily: MONO, fontSize: 7, letterSpacing: '.08em', color: r.clr, textAlign: 'center', lineHeight: 1.5 }}>{r.nm}</span>
            </div>
            <div style={{ flex: 1, position: 'relative', minHeight: r.h, alignSelf: 'stretch' }}>
              {d.gHours.map((k) => (
                <div key={k.key} style={{ position: 'absolute', top: 0, bottom: 0, left: k.leftP, borderLeft: '1px ' + k.st + ' var(--gt-line-0)' }} />
              ))}
              {d.nowOn ? <div style={{ position: 'absolute', top: 0, bottom: 0, left: d.nowXP, borderLeft: '1px solid var(--acc)', zIndex: 3, pointerEvents: 'none' }} /> : null}
              {r.blocks.map((n) => (
                <SchCover
                  key={n.key}
                  id={n.id}
                  cands={n.coverCands}
                  fall={n.fall}
                  onClick={() => open(n.id)}
                  style={{
                    position: 'absolute',
                    left: n.leftP,
                    top: n.top,
                    width: n.wP,
                    height: 56,
                    boxSizing: 'border-box',
                    border: '1px solid ' + n.bd,
                    borderRight: '1px ' + n.bRst + ' ' + n.bd,
                    overflow: 'hidden',
                    opacity: n.op,
                    zIndex: 2,
                    boxShadow: n.sh,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ position: 'absolute', inset: 0, background: SCH_SCRIM }} />
                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', padding: '7px 8px', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <span style={{ fontFamily: SANS, fontSize: 12, fontWeight: 500, color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.nm}</span>
                      <span style={{ fontFamily: PIXEL, fontSize: 7, color: n.tClr, transform: 'translateY(0.13em)' }}>{n.tLet}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontFamily: MONO, fontSize: '7.5px', letterSpacing: '.05em', color: n.rClr, whiteSpace: 'nowrap' }}>{n.rangeLive}</span>
                      <div style={{ marginLeft: 'auto' }}>
                        <SchFaceStack faces={n.qFaces} />
                      </div>
                    </div>
                  </div>
                </SchCover>
              ))}
            </div>
          </div>
        ))}
        <div style={{ borderTop: '1px solid var(--gt-border)' }} />
      </div>
    </div>
  );
}
